/**
 * @file App auth: SSO via Auth0 SPA (optional) + Help API session.
 * Roles come from the access token (API session); display name, email, sub, and picture come from the ID token when SSO is enabled.
 */
import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  apiFetch,
  clearTokens,
  getAccessToken,
  getApiBase,
  setAccessTokenOnly,
  setApiAccessTokenProvider,
  type SessionUserResponse,
} from "./api";
import { getSsoApiAudience, SSO_AUTH0_SCOPE } from "@/auth/sso-env";
import { isAuth0CallbackInProgress } from "@/lib/safe-navigation";

const STORAGE_USER = "sh_user";

/**
 * Signed-in user for the dashboard.
 * Under SSO, name/email/sub/`picture` come from the Auth0 **ID token**; `roles` come from the **access token** via the Help API session call.
 */
export interface AuthUser {
  sub: string;
  email: string;
  name?: string | null;
  /** ID token `picture` (e.g. Gravatar); optional. */
  picture?: string | null;
  roles?: string[];
  isSsoUser?: boolean;
}

function displayNameFromIdTokenClaims(claims: Record<string, unknown>): string | null {
  const given = typeof claims.given_name === "string" ? claims.given_name.trim() : "";
  const family = typeof claims.family_name === "string" ? claims.family_name.trim() : "";
  const combined = [given, family].filter(Boolean).join(" ");
  if (combined) {
    return combined;
  }
  const name = claims.name;
  if (typeof name === "string" && name.trim()) {
    return name.trim();
  }
  return null;
}

/**
 * Profile fields from the ID token; `roles` from API (access token).
 */
function buildSsoUserFromIdTokenAndApi(
  idClaims: Record<string, unknown> | null | undefined,
  apiSession: SessionUserResponse
): AuthUser {
  const roles = apiSession.roles ?? [];
  const subFromId = typeof idClaims?.sub === "string" ? idClaims.sub.trim() : "";
  const subFromApi = typeof apiSession.sub === "string" ? apiSession.sub.trim() : "";
  const sub = subFromId || subFromApi;
  if (!sub) {
    throw new Error("Invalid session response");
  }

  const emailFromId =
    typeof idClaims?.email === "string" && idClaims.email.includes("@")
      ? idClaims.email.trim().toLowerCase()
      : "";
  const emailFromApi =
    typeof apiSession.email === "string" && apiSession.email.includes("@")
      ? apiSession.email.trim().toLowerCase()
      : "";
  const email = (emailFromId || emailFromApi).trim();

  const nameFromId = idClaims ? displayNameFromIdTokenClaims(idClaims) : null;
  const name = nameFromId ?? apiSession.name ?? null;

  const picture =
    typeof idClaims?.picture === "string" && /^https?:\/\//i.test(idClaims.picture.trim())
      ? idClaims.picture.trim()
      : null;

  return {
    sub,
    email,
    name,
    picture,
    roles,
    isSsoUser: true,
  };
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** True when Auth0 SPA is active (env configured). */
  ssoEnabled: boolean;
  /** Auth0 SDK still resolving session / callback. */
  ssoInitializing: boolean;
  completingSignIn: boolean;
  exchangeError: string | null;
  clearExchangeError: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithSso: () => void;
  logout: () => boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

function readStoredUser(): AuthUser | null {
  if (typeof window === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(STORAGE_USER);
  if (!raw) {
    return null;
  }
  try {
    const u = JSON.parse(raw) as AuthUser & { id?: string };
    if (!u.sub && typeof u.id === "string") {
      return { ...u, sub: u.id };
    }
    return u as AuthUser;
  } catch {
    return null;
  }
}

/** Matches Auth0SsoProvider — audience + scope must stay aligned or Auth0 returns opaque tokens. */
function silentTokenOptions():
  | { authorizationParams: { audience: string; scope: string } }
  | undefined {
  const audience = getSsoApiAudience();
  if (!audience) {
    return undefined;
  }
  return {
    authorizationParams: {
      audience,
      scope: SSO_AUTH0_SCOPE,
    },
  };
}

function AuthProviderLocal({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      const { user: p } = await apiFetch<{ user: SessionUserResponse }>("/api/v1/auth/session", {
        method: "POST",
        json: {},
      });
      const next: AuthUser = {
        sub: p.sub,
        email: p.email && p.email.includes("@") ? p.email.trim().toLowerCase() : "",
        name: p.name,
        roles: p.roles ?? [],
        isSsoUser: p.isSsoUser ?? false,
      };
      localStorage.setItem(STORAGE_USER, JSON.stringify(next));
      setUser(next);
    } catch {
      // keep cached user
    }
  }, []);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    setUser(readStoredUser());
    setLoading(false);
    void refreshProfile();
  }, [refreshProfile]);

  const login = useCallback(async (email: string, password: string) => {
    void email;
    void password;
    throw new Error(
      "SSO is not configured. Set VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID, and VITE_AUTH0_API_AUDIENCE (same as API AUTH0_API_AUDIENCE)."
    );
  }, []);

  const loginWithSso = useCallback(() => {}, []);

  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem(STORAGE_USER);
    setUser(null);
    return false;
  }, []);

  const noopClear = useCallback(() => {}, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      ssoEnabled: false,
      ssoInitializing: false,
      completingSignIn: false,
      exchangeError: null,
      clearExchangeError: noopClear,
      login,
      loginWithSso,
      logout,
      refreshProfile,
    }),
    [user, loading, login, loginWithSso, logout, noopClear, refreshProfile]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function AuthProviderSso({ children }: { children: ReactNode }) {
  const {
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [exchangeError, setExchangeError] = useState<string | null>(null);
  const exchangeInFlight = useRef(false);
  const ssoRedirectStarted = useRef(false);

  const ssoInitializing = isLoading;
  const completingSignIn = Boolean(isAuthenticated && !user && !exchangeError);

  useEffect(() => {
    if (!isAuthenticated || isLoading) {
      setApiAccessTokenProvider(null);
      return;
    }
    const opts = silentTokenOptions();
    setApiAccessTokenProvider(() => getAccessTokenSilently(opts));
    return () => setApiAccessTokenProvider(null);
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const refreshProfile = useCallback(async () => {
    try {
      const [sessionRes, idClaims] = await Promise.all([
        apiFetch<{ user: SessionUserResponse }>("/api/v1/auth/session", {
          method: "POST",
          json: {},
        }),
        getIdTokenClaims().catch(() => undefined),
      ]);
      const p = sessionRes.user;
      if (!p || !Array.isArray(p.roles)) {
        return;
      }
      const next = buildSsoUserFromIdTokenAndApi(
        (idClaims as Record<string, unknown> | undefined) ?? null,
        p
      );
      localStorage.setItem(STORAGE_USER, JSON.stringify(next));
      setUser(next);
    } catch {
      // keep cached user
    }
  }, [getIdTokenClaims]);

  const exchangeApiSession = useCallback(async () => {
    const opts = silentTokenOptions();
    if (!opts) {
      throw new Error("VITE_AUTH0_API_AUDIENCE is not configured");
    }
    const accessToken = await getAccessTokenSilently(opts);
    const res = await fetch(`${getApiBase()}/api/v1/auth/session`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ accessToken }),
    });
    const body = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      throw new Error(typeof body.error === "string" ? body.error : "API session failed");
    }
    const apiUser = body.user as SessionUserResponse | undefined;
    if (!apiUser || !Array.isArray(apiUser.roles)) {
      throw new Error("Invalid session response");
    }
    const idClaims = (await getIdTokenClaims().catch(() => undefined)) as
      | Record<string, unknown>
      | undefined;
    const normalized = buildSsoUserFromIdTokenAndApi(idClaims ?? null, apiUser);
    setAccessTokenOnly(accessToken);
    localStorage.setItem(STORAGE_USER, JSON.stringify(normalized));
    setUser(normalized);
  }, [getAccessTokenSilently, getIdTokenClaims]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (!isAuthenticated) {
      clearTokens();
      localStorage.removeItem(STORAGE_USER);
      setUser(null);
      exchangeInFlight.current = false;
      setLoading(false);
      return;
    }
    if (getAccessToken()) {
      exchangeInFlight.current = false;
      setUser(readStoredUser());
      setLoading(false);
      void refreshProfile();
      return;
    }
    if (exchangeInFlight.current) {
      return;
    }
    exchangeInFlight.current = true;
    let cancelled = false;
    void (async () => {
      try {
        await exchangeApiSession();
        if (!cancelled) {
          setExchangeError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setExchangeError(e instanceof Error ? e.message : "Could not complete sign-in with the API");
        }
        exchangeInFlight.current = false;
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, exchangeApiSession, refreshProfile]);

  const clearExchangeError = useCallback(() => {
    setExchangeError(null);
    ssoRedirectStarted.current = false;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    void email;
    void password;
    throw new Error("Password sign-in is disabled. Use SSO.");
  }, []);

  const loginWithSso = useCallback(() => {
    if (ssoRedirectStarted.current || isAuth0CallbackInProgress()) {
      return;
    }
    ssoRedirectStarted.current = true;
    setExchangeError(null);
    const path = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    const returnTo =
      path === "/login" || path === "/" ? "/dashboard" : `${path}${search}${hash}`;
    const audience = getSsoApiAudience();
    void loginWithRedirect({
      appState: { returnTo },
      authorizationParams: audience
        ? {
            audience,
            scope: SSO_AUTH0_SCOPE,
          }
        : undefined,
    });
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem(STORAGE_USER);
    setUser(null);
    exchangeInFlight.current = false;
    ssoRedirectStarted.current = false;
    if (isAuthenticated) {
      void auth0Logout({
        logoutParams: { returnTo: window.location.origin },
      });
      return true;
    }
    return false;
  }, [isAuthenticated, auth0Logout]);

  const value = useMemo(
    () => ({
      user,
      loading,
      ssoEnabled: true,
      ssoInitializing,
      completingSignIn,
      exchangeError,
      clearExchangeError,
      login,
      loginWithSso,
      logout,
      refreshProfile,
    }),
    [
      user,
      loading,
      ssoInitializing,
      completingSignIn,
      exchangeError,
      clearExchangeError,
      login,
      loginWithSso,
      logout,
      refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({
  children,
  ssoConfigured = false,
}: {
  children: ReactNode;
  ssoConfigured?: boolean;
}) {
  if (ssoConfigured) {
    return <AuthProviderSso>{children}</AuthProviderSso>;
  }
  return <AuthProviderLocal>{children}</AuthProviderLocal>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
